import React, { useState, useEffect, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/apiClient";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { WebsiteTagsEditor, WebsiteTagObject } from "../WebsiteTagsEditor";

interface ReviewTagsSectionProps {
  clientId: string;
  mediaItem: MediaItem;
  /** Called after a successful save so the queue's in-memory item can sync. */
  onPersisted?: (itemId: number, tags: WebsiteTagObject[]) => void;
}

export const ReviewTagsSection: React.FC<ReviewTagsSectionProps> = ({
  clientId,
  mediaItem,
  onPersisted,
}) => {
  const [tags, setTags] = useState<WebsiteTagObject[]>(mediaItem.websiteTags);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">(
    "idle"
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The edit awaiting persistence, bound to the item it belongs to. Bound per
  // item (not a shared ref) so a save can never be written to the wrong item.
  const pendingRef = useRef<{ itemId: number; tags: WebsiteTagObject[] } | null>(
    null
  );

  // The client's distinct website tags, for quick-pick. Independent of the
  // media queue so the full set is always available.
  const { data: suggestions = [] } = useQuery({
    queryKey: ["website-tags", clientId],
    queryFn: async () => {
      const res = await apiClient.get<{ tags: string[] }>(
        `/client/${clientId}/media/website-tags`
      );
      return res.tags;
    },
    enabled: !!clientId,
    staleTime: 60 * 1000,
  });

  const persistTags = useCallback(
    async (itemId: number, nextTags: WebsiteTagObject[]) => {
      setIsSaving(true);
      setSaveStatus("saving");
      try {
        await apiClient.patch(
          `/client/${clientId}/media/${itemId}/website-tags`,
          { tags: nextTags }
        );
        setSaveStatus("saved");
        onPersisted?.(itemId, nextTags);
      } catch (err) {
        console.error("Failed to save website tags", err);
        setSaveStatus("idle");
      } finally {
        setIsSaving(false);
      }
    },
    [clientId, onPersisted]
  );

  // Immediately persist any queued edit. Called before leaving an item
  // (navigation or unmount) so a debounced save is flushed, never dropped.
  const flushPending = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) {
      void persistTags(pending.itemId, pending.tags);
    }
  }, [persistTags]);

  // Re-seed from the current item when navigating. The cleanup flushes the
  // previous item's pending save (on both navigation and unmount) so switching
  // items or closing the modal before the debounce fires never loses an edit.
  useEffect(() => {
    setTags(mediaItem.websiteTags);
    setSaveStatus("idle");
    return () => {
      flushPending();
    };
  }, [mediaItem.id, mediaItem.websiteTags, flushPending]);

  const handleTagsChange = useCallback(
    (nextTags: WebsiteTagObject[]) => {
      setTags(nextTags);
      setSaveStatus("idle");
      pendingRef.current = { itemId: mediaItem.id, tags: nextTags };

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        const pending = pendingRef.current;
        pendingRef.current = null;
        if (pending) {
          void persistTags(pending.itemId, pending.tags);
        }
      }, 500);
    },
    [mediaItem.id, persistTags]
  );

  return (
    <div className="space-y-2 pt-3 border-t border-gray-700">
      <div className="flex items-center justify-between text-gray-300">
        <span className="text-white/70">Tags</span>
      </div>
      <WebsiteTagsEditor
        tags={tags}
        onTagsChange={handleTagsChange}
        isSaving={isSaving}
        saveStatus={saveStatus}
        suggestions={suggestions}
        tone="dark"
      />
    </div>
  );
};
